import { apiClient } from '@/api/client'
import { ApiError, parseProblemDetails } from '@/api/problemDetails'
import type { components } from '@/api/schema'

/** Status manual do atendente: `available` (Disponível) ou `away` (Ausente). */
export type PresenceStatus = 'available' | 'away'

/** Por que a equipe está (in)disponível agora. */
export type TeamAvailabilityState = 'available' | 'outside_business_hours' | 'no_operator_online'

export interface TeamAvailability {
  readonly state: TeamAvailabilityState
  readonly onlineOperators: number
  readonly businessHoursEnabled: boolean
  readonly nextOpeningAt: string | null
  /** Quando o cliente é respondido, ex.: "amanhã a partir das 08:00" (texto do servidor, pt-BR). */
  readonly returnText: string
}

export interface PresenceOverview {
  readonly myStatus: PresenceStatus
  readonly iAmOnline: boolean
  readonly team: TeamAvailability
  /** Chamados aguardando alguém assumir. */
  readonly waitingTickets: number
}

function toNumber(value: number | string): number {
  return typeof value === 'number' ? value : Number(value)
}

export function mapTeamAvailability(
  dto: components['schemas']['TeamAvailabilityResponse'],
): TeamAvailability {
  const state: TeamAvailabilityState =
    dto.state === 'available' || dto.state === 'outside_business_hours'
      ? dto.state
      : 'no_operator_online'

  return {
    state,
    onlineOperators: toNumber(dto.onlineOperators),
    businessHoursEnabled: dto.businessHoursEnabled,
    nextOpeningAt: dto.nextOpeningAt,
    returnText: dto.returnText,
  }
}

function mapOverview(dto: components['schemas']['PresenceOverviewResponse']): PresenceOverview {
  return {
    myStatus: dto.myStatus === 'available' ? 'available' : 'away',
    iAmOnline: dto.iAmOnline,
    team: mapTeamAvailability(dto.team),
    waitingTickets: toNumber(dto.waitingTickets),
  }
}

function unexpectedFormatError(status: number): ApiError {
  return new ApiError({
    title: 'A resposta da Api nao tem o formato esperado.',
    status,
    errorCode: 'internal_error',
    correlationId: '',
  })
}

/** Mantém o atendente online enquanto o app está aberto; devolve o status da equipe e a fila. */
export async function sendHeartbeat(tenantId: string): Promise<PresenceOverview> {
  const result = await apiClient.POST('/api/v1/tenants/{tenantId}/presence/heartbeat', {
    params: { path: { tenantId } },
  })

  if (!result.response.ok) {
    throw new ApiError(parseProblemDetails(result.error, result.response.status))
  }

  if (result.data === undefined) {
    throw unexpectedFormatError(result.response.status)
  }

  return mapOverview(result.data)
}

export async function setPresenceStatus(
  tenantId: string,
  status: PresenceStatus,
): Promise<PresenceOverview> {
  const result = await apiClient.PUT('/api/v1/tenants/{tenantId}/presence/status', {
    params: { path: { tenantId } },
    body: { status },
  })

  if (!result.response.ok) {
    throw new ApiError(parseProblemDetails(result.error, result.response.status))
  }

  if (result.data === undefined) {
    throw unexpectedFormatError(result.response.status)
  }

  return mapOverview(result.data)
}

/** Sai da contagem de atendentes online na hora (logout). Best-effort. */
export async function disconnectPresence(tenantId: string): Promise<void> {
  await apiClient.POST('/api/v1/tenants/{tenantId}/presence/disconnect', {
    params: { path: { tenantId } },
  })
}
